<template>
  <div
    ref="playerRoot"
    class="w-full h-full rounded-3xl px-6 text-slate-100 flex flex-col"
    :class="playerSize <= 80 ? 'py-4' : 'py-6'"
  >
    <div class="flex items-center justify-between mb-4">
      <!-- <div>
        <h1 class="text-lg md:text-xl font-semibold">仿网易云 · 唱片播放器</h1>
        <p class="text-xs text-slate-400 mt-1">
          点击唱片或使用底部原生控件播放/暂停
        </p>
      </div> -->

      <div
        v-if="!props.showNativeControls"
        class="flex items-center"
        @click="togglePlay"
      >
        <UIcon
          v-if="isPlaying"
          name="material-symbols-light:pause-circle-outline-rounded"
          class="size-10 cursor-pointer text-primary"
        />
        <UIcon
          v-else
          name="material-symbols-light:play-circle-outline-rounded"
          class="size-10 cursor-pointer text-primary"
        />

        <!-- <div>
          <h1
            class="text-lg md:text-xl font-semibold"
            :class="isPlaying ? 'text-slate-100' : 'text-red-500'"
          >
            {{ isPlaying ? "正在播放" : "点击播放" }}
          </h1>
          <p class="text-xs text-slate-400 mt-1">点击唱片或此按钮控制播放</p>
        </div> -->
      </div>
    </div>

    <div class="flex-1 flex items-center justify-center">
      <div class="relative" :style="recordWrapperStyle" @click="togglePlay">
        <div
          class="absolute top-1/2 left-1/2 rounded-full cursor-pointer vinyl-disc bg-cover bg-center"
          :style="discStyle"
        ></div>

        <div
          class="absolute origin-top-left needle-base"
          :style="needleWrapperStyle"
        >
          <div
            class="needle-arm"
            :class="isPlaying ? 'needle-playing' : 'needle-paused'"
            :style="needleStyle"
          >
            <div
              class="needle-image"
              :style="{
                backgroundImage: `url('${cdnUrl}/ai/public/svg/player_needle.svg')`,
              }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <div
      :class="
        props.showNativeControls
          ? 'mt-auto w-full flex justify-center pt-0'
          : 'hidden'
      "
    >
      <audio
        ref="audioRef"
        class="w-full py-0!"
        :src="effectiveAudioSrc"
        controls
        @play="handlePlay"
        @pause="handlePause"
        @ended="handleEnded"
        @loadedmetadata="handleLoadedMetadata"
        preload="metadata"
        :style="{
          height: playerSize <= 80 ? '30px' : '',
        }"
      ></audio>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import { useElementSize } from "@vueuse/core";

/** 将裸 PCM 字节包装为 WAV（浏览器 <audio> 可播），pcmBuffer 为纯样本数据 */
function pcmBytesToWavBlob(
  pcmBuffer,
  sampleRate,
  numChannels,
  bitsPerSample
) {
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = pcmBuffer.byteLength;
  const headerSize = 44;
  const out = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(out);
  const u8 = new Uint8Array(out);

  const writeStr = (offset, s) => {
    for (let i = 0; i < s.length; i++) {
      view.setUint8(offset + i, s.charCodeAt(i));
    }
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  u8.set(new Uint8Array(pcmBuffer), headerSize);
  return new Blob([out], { type: "audio/wav" });
}

const props = defineProps({
  audioSrc: {
    type: String,
    required: true,
  },
  /** auto：根据 URL 是否含 .pcm 判断；pcm：按裸 PCM 拉取并封装 WAV；standard：始终直连 audioSrc */
  audioFormat: {
    type: String,
    default: "auto",
    validator: (v) => ["auto", "pcm", "standard"].includes(v),
  },
  pcmSampleRate: {
    type: Number,
    default: 44100,
  },
  pcmChannels: {
    type: Number,
    default: 2,
  },
  pcmBitsPerSample: {
    type: Number,
    default: 16,
  },
  discImage: {
    type: String,
    default: "/ai/public/svg/player_bg.png",
  },
  playerSize: {
    type: Number,
    default: 0,
  },
  // 新增参数：是否显示底部原生进度条，默认显示
  showNativeControls: {
    type: Boolean,
    default: true,
  },
});

const cdnUrl = useRuntimeConfig().public.cdnUrl;

const isPcmMode = computed(() => {
  if (props.audioFormat === "pcm") return true;
  if (props.audioFormat === "standard") return false;
  const u = props.audioSrc || "";
  return /\.pcm(\?|#|$)/i.test(u);
});

/** 非 PCM 直连；PCM 在客户端拉取后封装为 WAV 的 blob URL */
const effectiveAudioSrc = ref(props.audioSrc);
let pcmObjectUrl = null;
let pcmLoadGeneration = 0;

function revokePcmObjectUrl() {
  if (pcmObjectUrl) {
    URL.revokeObjectURL(pcmObjectUrl);
    pcmObjectUrl = null;
  }
}

async function syncEffectiveAudioSrc() {
  if (import.meta.server) {
    effectiveAudioSrc.value = props.audioSrc;
    return;
  }

  if (!isPcmMode.value) {
    revokePcmObjectUrl();
    effectiveAudioSrc.value = props.audioSrc;
    return;
  }

  const gen = ++pcmLoadGeneration;
  revokePcmObjectUrl();
  effectiveAudioSrc.value = "";

  try {
    const res = await fetch(props.audioSrc);
    if (!res.ok) {
      throw new Error(`PCM 请求失败 ${res.status}`);
    }
    const arrayBuffer = await res.arrayBuffer();
    if (gen !== pcmLoadGeneration) return;

    const blob = pcmBytesToWavBlob(
      arrayBuffer,
      props.pcmSampleRate,
      props.pcmChannels,
      props.pcmBitsPerSample
    );
    pcmObjectUrl = URL.createObjectURL(blob);
    effectiveAudioSrc.value = pcmObjectUrl;
  } catch (e) {
    console.error("PCM 加载/封装失败：", e);
    if (gen === pcmLoadGeneration) {
      effectiveAudioSrc.value = props.audioSrc;
    }
  }
}

watch(
  () => [
    props.audioSrc,
    props.audioFormat,
    props.pcmSampleRate,
    props.pcmChannels,
    props.pcmBitsPerSample,
  ],
  () => {
    syncEffectiveAudioSrc();
  },
  { immediate: true }
);

const audioRef = ref(null);
const isPlaying = ref(false);

// 当前旋转角度（度数）
const rotation = ref(0);

// 当前时间 / 总时长（秒）
const currentTime = ref(0);
const duration = ref(0);

// 每秒旋转多少度（转速）
const DEGREES_PER_SECOND = 30;

const BASE_SIZE = 240;
const BASE_NEEDLE_WIDTH = 110;
const BASE_NEEDLE_HEIGHT = 230;
const BASE_NEEDLE_TOP_OFFSET = 34;
const BASE_NEEDLE_RIGHT_OFFSET = 16;

// requestAnimationFrame 的 id
let rafId = null;

// 点击唱片切换播放 / 暂停
const togglePlay = () => {
  const audio = audioRef.value;
  if (!audio) return;

  if (audio.paused) {
    audio.play().catch((e) => {
      console.error("播放失败：", e);
    });
  } else {
    audio.pause();
  }
};

// 开始用 requestAnimationFrame 同步旋转
const startProgressLoop = () => {
  cancelAnimationFrame(rafId);

  const loop = () => {
    const audio = audioRef.value;
    if (!audio) {
      rafId = null;
      return;
    }

    // 用真实的 currentTime 驱动显示 & 旋转，始终和音乐同步
    currentTime.value = audio.currentTime || 0;
    duration.value = audio.duration || 0;
    rotation.value = currentTime.value * DEGREES_PER_SECOND;

    if (!audio.paused) {
      rafId = requestAnimationFrame(loop);
    } else {
      rafId = null;
    }
  };

  rafId = requestAnimationFrame(loop);
};

// 播放事件
const handlePlay = () => {
  isPlaying.value = true;
  startProgressLoop();
};

// 暂停事件
const handlePause = () => {
  isPlaying.value = false;
  // 不强制清 raf，让循环自己检测 audio.paused 后停掉
};

// 结束事件
const handleEnded = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  rotation.value = 0;
  cancelAnimationFrame(rafId);
  rafId = null;
};

// 元数据加载完成时拿到总时长
const handleLoadedMetadata = () => {
  const audio = audioRef.value;
  if (audio) {
    duration.value = audio.duration || 0;
  }
};

const playerRoot = ref(null);
const { height: containerHeight } = useElementSize(playerRoot, {
  width: BASE_SIZE,
  height: BASE_SIZE,
});

const shouldAutoSize = computed(() => {
  return (
    props.playerSize === undefined ||
    props.playerSize === null ||
    props.playerSize <= 0
  );
});

const autoPlayerSize = computed(() => {
  const measuredHeight = containerHeight.value || 0;
  const targetHeight = measuredHeight > 0 ? measuredHeight : BASE_SIZE;
  return targetHeight * 0.7;
});

const playerSize = computed(() => {
  if (typeof window !== "undefined" && window.innerWidth < 400) {
    return 80;
  }

  if (shouldAutoSize.value) {
    return autoPlayerSize.value;
  }

  return props.playerSize;
});

const scaleRatio = computed(() => playerSize.value / BASE_SIZE);

const recordWrapperStyle = computed(() => ({
  width: `${playerSize.value}px`,
  height: `${playerSize.value}px`,
}));

// 唱片样式（同音乐同步旋转）
const discStyle = computed(() => ({
  width: `${playerSize.value * 0.75}px`,
  height: `${playerSize.value * 0.75}px`,
  transform: `translate(-50%, -50%) rotate(${rotation.value}deg)`,
  backgroundImage: `url('${cdnUrl}${props.discImage}')`,
}));

const needleStyle = computed(() => ({
  width: `${BASE_NEEDLE_WIDTH * scaleRatio.value}px`,
  height: `${BASE_NEEDLE_HEIGHT * scaleRatio.value}px`,
}));

const needleWrapperStyle = computed(() => ({
  top: `${-BASE_NEEDLE_TOP_OFFSET * scaleRatio.value}px`,
  right: `${BASE_NEEDLE_RIGHT_OFFSET * scaleRatio.value}px`,
}));

onUnmounted(() => {
  if (audioRef.value) {
    audioRef.value.pause();
  }
  revokePcmObjectUrl();
  cancelAnimationFrame(rafId);
  rafId = null;
});
</script>

<style scoped>
/* 唱针整体的 transform 原点在右上角，让它像从顶部摆下来 */
/* .needle-base {
  transform-origin: 49% 20%;
} */

/* 唱针 */
.needle-arm {
  transform-origin: 61% 20%;
  transition: transform 0.35s ease-out;
}

.needle-image {
  width: 100%;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

/* 暂停时唱针往外收一点 */
.needle-paused {
  transform: rotate(-45deg) translateX(4px);
  /* background-color: red; */
}

/* 播放时唱针压在唱片上 */
.needle-playing {
  transform: rotate(-10deg);
  /* background-color: blue; */
}

/* 让浏览器为频繁的 transform 做好优化 */
.vinyl-disc {
  will-change: transform;
}

audio::-webkit-media-controls-panel {
  background-color: transparent;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  -webkit-box-shadow: none !important;
}

/* 移除时间轴的背景 */
audio::-webkit-media-controls-timeline {
  background-color: transparent;
  border: none;
}

/* 移除音量滑块的背景 */
audio::-webkit-media-controls-volume-slider {
  background-color: transparent;
}

/* 移除所有内部元素的背景 */
audio::-webkit-media-controls-enclosure {
  background-color: transparent;
}

/* Firefox 样式 */
audio::-moz-media-controls-panel {
  background-color: transparent;
}

/* 通用样式 */
audio {
  background-color: transparent;
  border: none;
  outline: none;
  box-shadow: none;
}
</style>
